// Copyright ©️ 2026 eVolve MEP, LLC

import React, { type ReactNode, useState, useCallback, useRef, useMemo, useEffect } from 'react';

import { Box, type BoxProps } from '@mantine/core';
import type { GridApi } from 'ag-grid-enterprise';

import { compactRowHeight } from 'helpers/ag-grid/baseColumnDef';

export type AgGridContainerProps = Omit<BoxProps, 'children'> & {
  children: (applyWrap: (event: TriggerEvent) => void) => ReactNode;
};

const TableShadow = ({
  position,
  canScroll,
  headerHeight,
  rightHeaderWidth,
  leftHeaderWidth,
}: {
  position: 'left' | 'right';
  canScroll: boolean;
  leftHeaderWidth: number;
  rightHeaderWidth: number;
  headerHeight: number;
}) => (
  <div
    aria-hidden
    className={`ag-grid-custom-container-shadow ${canScroll ? 'active' : ''}`}
    style={{
      position: 'absolute',
      top: 0,
      height: headerHeight + 1, // + containerHeight,
      left: position === 'left' ? (leftHeaderWidth > 0 ? leftHeaderWidth + 1 : leftHeaderWidth) : undefined,
      right: position === 'right' ? (rightHeaderWidth > 0 ? rightHeaderWidth + 1 : rightHeaderWidth) : undefined,
      width: 18,
      pointerEvents: 'none',
      background: `linear-gradient(to ${position === 'left' ? 'right' : 'left'}, rgba(0,0,0,0.08), rgba(0,0,0,0))`,
      borderTopLeftRadius:
        position === 'left' && leftHeaderWidth <= 1 ? 'var(--ag-wrapper-border-radius, 8px)' : undefined,
      borderTopRightRadius:
        position === 'right' && rightHeaderWidth <= 1 ? 'var(--ag-wrapper-border-radius, 8px)' : undefined,
    }}
  />
);

type TriggerEvent = {
  api: GridApi;
};

/**
 * General AG Grid container.
 * Shows shadows on the left and right of the grid when scrolling is available.
 */
export const AgGridContainer = ({
  children,
  h = '100%',
  bdrs = 'var(--ag-border-radius)',
  pos = 'relative',
  style,
  ...boxProps
}: AgGridContainerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const [leftHeaderWidth, setLeftHeaderWidth] = useState(0);
  const [rightHeaderWidth, setRightHeaderWidth] = useState(0);
  const [headerHeight, setHeaderHeight] = useState<number>(compactRowHeight);
  const [containerHeight, setContainerHeight] = useState(0);

  const calculateShadowPositions = useCallback(() => {
    setLeftHeaderWidth((v) => {
      // The sidebar (configured in `useSideBar()`) *should* always be pinned to the left.
      const sideBarWidth = containerRef.current?.querySelector(`.ag-side-bar`)?.clientWidth ?? 0;
      const pinnedLeftWidth = containerRef.current?.querySelector(`.ag-pinned-left-header`)?.clientWidth ?? 0;
      return sideBarWidth + pinnedLeftWidth || v;
    });
    setRightHeaderWidth((v) => containerRef.current?.querySelector(`.ag-pinned-right-header`)?.clientWidth ?? v);
    setHeaderHeight((v) => containerRef.current?.querySelector(`.ag-header`)?.clientHeight ?? v);
    setContainerHeight((v) => containerRef.current?.querySelector(`.ag-center-cols-container`)?.clientHeight ?? v);
  }, []);
  useEffect(calculateShadowPositions, [calculateShadowPositions]);

  const applyWrap = useCallback(
    (event: TriggerEvent, retry = 0) => {
      const container = containerRef.current;
      if (!container) return undefined;

      // AG Grid’s horizontal scrollable viewport for center columns
      const viewport = container.querySelector<HTMLDivElement>('.ag-center-cols-viewport');
      if (!viewport) {
        // oxlint-disable-next-line react/immutability
        if (retry < 10) setTimeout(() => applyWrap(event, retry + 1), retry * 10);
        return undefined;
      }

      const updateShadows = () => {
        const { scrollLeft, clientWidth, scrollWidth } = viewport;
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
      };
      updateShadows();
      calculateShadowPositions();

      const ro = new ResizeObserver(() => {
        updateShadows();
        calculateShadowPositions();
      });

      // Recalculate on scroll
      viewport.addEventListener('scroll', updateShadows, { passive: true });
      // Recalculate on size/content changes
      ro.observe(viewport);
      // Recalculate on column resize
      // Also observe the center container which can change when columns/pinning update
      const centerContainer = container.querySelector<HTMLDivElement>('.ag-center-cols-container');
      if (centerContainer) ro.observe(centerContainer);

      return () => {
        viewport.removeEventListener('scroll', updateShadows);
        ro.disconnect();
      };
    },
    [calculateShadowPositions],
  );

  const shadowProps = useMemo(
    () => ({
      leftHeaderWidth,
      rightHeaderWidth,
      headerHeight,
      containerHeight,
    }),
    [containerHeight, headerHeight, leftHeaderWidth, rightHeaderWidth],
  );

  return (
    <Box
      className="ag-grid-custom-container"
      ref={containerRef}
      h={h}
      bdrs={bdrs}
      pos={pos}
      style={{
        overflow: 'hidden',
        // TODO: `.style` can now be a function, need to handle it
        // oxlint-disable-next-line typescript/no-misused-spread
        ...style,
      }}
      {...boxProps}
    >
      {children(applyWrap)}

      <TableShadow {...shadowProps} position="left" canScroll={canScrollLeft} />
      <TableShadow {...shadowProps} position="right" canScroll={canScrollRight} />
    </Box>
  );
};
